/**
 * Fuel-stop optimizer — the "Gas Station Problem" applied to per-state pricing.
 *
 * ## Why this exists
 *
 * The state-breakdown math computes the *expected blended cost* assuming the
 * driver buys gallons proportional to per-state mileage. Real drivers don't
 * fuel that way — they fill up when their tank runs low, and which state's
 * price they pay depends entirely on *where they are when they need fuel*.
 *
 * A driver crossing from cheap Oregon ($3.50/g) into expensive California
 * ($4.89/g) can save ~$1.39/gal on every gallon they top off in Oregon
 * before crossing the line. That savings doesn't appear in the blended math.
 *
 * ## What this module computes
 *
 * Given a route's state-by-state breakdown, a tank capacity, and a starting
 * fuel level, we compute two scenarios:
 *
 *   1. **Smart plan** — optimal greedy fueling: proactively fill in cheap
 *      states even when the tank is half-full, knowing expensive territory
 *      lies ahead. Buy minimum at expensive stops.
 *   2. **Naive plan** — driver refuels when the tank hits a low threshold,
 *      paying whatever the state's price is at that moment. This is the
 *      apples-to-apples baseline that the smart plan is compared against.
 *
 * Both scenarios use the same starting fuel and arrive at the same destination
 * with the same total gallons burned. The only difference is *which state's
 * price* each refueled gallon was bought at — which is where savings come from.
 *
 * ## Algorithm
 *
 * Classical greedy "gas station problem" (Khuller, Malekian, Mestre 2007).
 * Linear-time. At each potential fuel stop:
 *
 *   1. Look ahead one tank-range (capacity × MPG)
 *   2. Find the cheapest price in that window
 *   3. If current state ≤ cheapest in window: fill to capacity
 *   4. Else: buy just enough to reach the cheaper state (+ reserve)
 *
 * State-pricing constraint: we don't have station-level prices, only state
 * averages. So "stops" are placed at state entry points — that's the only
 * granularity we have honest data for. Output is framed as "fill in this
 * zone" rather than "fill at Pilot #482."
 */

import type { StateSegment } from './types'

/** Reserve fuel that we never want to dip below mid-segment.
 *
 * Tank capacity × this fraction = "empty" for planning purposes. Driving past
 * this point is the optimizer's "you ran dry" condition — in reality drivers
 * stop at ~⅛ tank, not on fumes. Keeping a safety reserve also accommodates
 * the fact that state-average pricing isn't station-precise: if a recommended
 * stop's actual stations are pricier than predicted, the reserve gives the
 * driver room to roll into the next exit.
 */
const SAFETY_RESERVE_FRACTION = 0.1

/** Naive driver refuels when the tank drops to this fraction of capacity. */
const NAIVE_REFUEL_THRESHOLD_FRACTION = 0.25

/** Minimum gallons a recommended top-off must add to be worth recommending.
 *
 * If the algorithm wants to "top off" with 3 gallons before a state line,
 * that's a useless recommendation — the driver isn't pulling over for that.
 */
const MIN_FILL_GALLONS = 5

/**
 * A single recommended fuel stop on the route.
 *
 * `kind` distinguishes:
 *   - `smart`: an opportunistic top-off in a cheap zone before entering a
 *     more expensive area — driver chooses to stop here for savings
 *   - `forced`: a required stop because the tank would otherwise run dry
 *     before reaching anything cheaper
 */
export interface FuelStop {
	/** 1-indexed stop number for UI labels and map markers */
	index: number
	kind: 'smart' | 'forced'
	/** State code where this stop happens (e.g., 'OR', 'CA') */
	stateCode: string
	/** Human-readable state name */
	state: string
	/** Distance from origin in miles where this stop occurs */
	mileFromStart: number
	/** Pump price ($/gal) at this stop */
	pricePerGallon: number
	/** Gallons recommended to buy at this stop */
	gallons: number
	/** Total cost of this fill ($) */
	cost: number
}

/**
 * Complete fuel plan output. Includes the stop sequence plus the headline
 * comparison numbers that justify the entire feature ("you save $X").
 */
export interface FuelStopPlan {
	stops: FuelStop[]
	/** Total cost following the smart plan ($) — sum of all recommended stops */
	smartTotalCost: number
	/**
	 * Total cost a naive driver would pay refueling when low without planning.
	 * This is the apples-to-apples comparison baseline.
	 */
	naiveTotalCost: number
	/**
	 * Dollar savings from following the smart plan vs. naive fueling.
	 * Floored at 0 — the planner never recommends a worse-than-naive sequence.
	 */
	savings: number
	/** Total gallons that will be purchased across all smart-plan stops */
	totalGallonsBought: number
	/**
	 * Reason the plan couldn't be computed, if applicable. Set when the
	 * route is too long for the tank and there's no feasible fueling
	 * sequence (e.g., a single state segment longer than tank range).
	 */
	infeasibleReason: string | null
}

/** Internal: a "fuel decision point" — where the driver could choose to refuel. */
interface DecisionPoint {
	stateCode: string
	state: string
	pricePerGallon: number
	/** Distance from origin where this state begins */
	mileFromStart: number
	/** Distance from origin where this state ends */
	mileEnd: number
	/** Effective MPG inside this state (already grade-adjusted) */
	effectiveMpg: number
}

/**
 * Build the linear sequence of decision points from per-state segments.
 *
 * Each state along the route becomes one decision point — the driver could
 * (in principle) refuel anywhere inside it, and the price is constant for
 * the entire stretch.
 */
function buildDecisionPoints(segments: StateSegment[]): DecisionPoint[] {
	const points: DecisionPoint[] = []
	let cursor = 0
	for (const seg of segments) {
		points.push({
			stateCode: seg.stateCode,
			state: seg.state,
			pricePerGallon: seg.fuelPrice,
			mileFromStart: cursor,
			mileEnd: cursor + seg.distanceMiles,
			effectiveMpg: seg.effectiveMpg,
		})
		cursor += seg.distanceMiles
	}
	return points
}

/**
 * Find the cheapest decision point within `currentMile + range` (inclusive),
 * starting from `fromIdx`. Returns the index of the cheapest reachable state
 * — including the current one (so if "here" is the cheapest in range, the
 * caller fills here).
 */
function findCheapestInWindow(
	points: DecisionPoint[],
	fromIdx: number,
	currentMile: number,
	range: number,
): number {
	const horizon = currentMile + range
	let bestIdx = fromIdx
	let bestPrice = Number.POSITIVE_INFINITY
	for (let i = fromIdx; i < points.length; i++) {
		const p = points[i]
		if (!p) continue
		if (p.mileFromStart > horizon) break
		if (p.pricePerGallon < bestPrice) {
			bestPrice = p.pricePerGallon
			bestIdx = i
		}
	}
	return bestIdx
}

/**
 * Pre-flight feasibility check: is there a single state longer than what a
 * full tank can span? If so, no fueling sequence works (you'd run dry inside
 * the long state with no stations available).
 *
 * KNOWN BUG (Codex P2, 2026-04-19) — fix before unshelving:
 * This check is too strict for long single-state routes. Our pricing model
 * is state-level, not station-level — so multiple fills within the same
 * state are valid (and would all hit the same per-state average price).
 * A 1500-mi Texas-only route with a 200-gal tank should be feasible via
 * repeated TX fills, but this returns infeasibleReason. Repro:
 *   planFuelStops([{stateCode:'TX', distanceMiles:1500, fuelPrice:3.2, effectiveMpg:6, ...}], 200, 200)
 *   → infeasibleReason: 'Texas is 1500 mi long, exceeds tank range'
 *
 * Fix direction: replace the per-state-segment check with a route-wide
 * check that the route is at least conceivably traversable (i.e., the
 * required fueling cadence ≤ the tank's range), and let the planner
 * itself emit multiple in-state stops as needed.
 */
function checkFeasibility(
	points: DecisionPoint[],
	tankCapacity: number,
	reserve: number,
): string | null {
	for (const p of points) {
		const segMiles = p.mileEnd - p.mileFromStart
		const usableRange = (tankCapacity - reserve) * p.effectiveMpg
		if (segMiles > usableRange) {
			return `${p.state} is ${Math.round(segMiles)} mi long, exceeds tank range`
		}
	}
	return null
}

/**
 * Result of the per-state fueling decision: how much to buy and whether the
 * fill is a strategic top-off or a forced fill to avoid running dry.
 *
 * Extracted to a typed return so `computeSmartPlan` stays under the project's
 * cognitive-complexity limit (15) — the decision logic is rich enough on its
 * own that inlining it pushes the loop over.
 */
interface FillDecision {
	buyGallons: number
	mustFill: boolean
}

/**
 * Decide how much fuel to buy at the current state.
 *
 * Unified target-distance formula:
 *   - If a cheaper state lies within tank-range ahead → target = miles to it.
 *     Buy just enough to reach it (+ reserve), saving tank room for cheaper
 *     gallons later.
 *   - Otherwise (we're at the cheapest in lookahead, or end-of-route) →
 *     target = miles to destination. Buy to cover the rest of the trip, but
 *     never more than tank capacity and never more than needed to finish
 *     (no over-buying expensive fuel that ends up unused).
 *
 * Includes the "must fill" edge guard: if the optimization said skip this
 * fill but we'd run dry mid-state, force the minimum needed to clear.
 */
function decideFill(
	here: DecisionPoint,
	cheapestAhead: DecisionPoint,
	mile: number,
	totalMiles: number,
	currentFuel: number,
	tankCapacity: number,
	reserve: number,
): FillDecision {
	const milesInState = here.mileEnd - mile
	const fuelToClearState = milesInState / here.effectiveMpg
	const usableFuel = currentFuel - reserve
	const mustFill = usableFuel < fuelToClearState

	const isCheapestHere = here.pricePerGallon <= cheapestAhead.pricePerGallon
	const targetDistance = isCheapestHere ? totalMiles - mile : cheapestAhead.mileFromStart - mile
	const gallonsForTarget = targetDistance / here.effectiveMpg + reserve
	const desiredFuel = Math.min(tankCapacity, gallonsForTarget)
	let buyGallons = Math.max(0, desiredFuel - currentFuel)

	// Edge guard: optimization wanted skip, but we'd run dry — force min fill
	if (mustFill && buyGallons < fuelToClearState - usableFuel) {
		buyGallons = Math.min(fuelToClearState - usableFuel + reserve, tankCapacity - currentFuel)
	}

	return { buyGallons, mustFill }
}

/** Build a FuelStop record from raw values. Pulled out for arithmetic clarity. */
function buildStop(
	indexN: number,
	kind: FuelStop['kind'],
	state: DecisionPoint,
	mile: number,
	gallons: number,
): FuelStop {
	return {
		index: indexN,
		kind,
		stateCode: state.stateCode,
		state: state.state,
		mileFromStart: Math.round(mile),
		pricePerGallon: state.pricePerGallon,
		gallons: Math.round(gallons * 10) / 10,
		cost: Math.round(gallons * state.pricePerGallon * 100) / 100,
	}
}

/**
 * Compute the smart plan via classical greedy lookahead. Walks the route
 * state-by-state, asks `decideFill` at each entry whether to buy and how
 * much, then burns through the state to its end.
 */
function computeSmartPlan(
	points: DecisionPoint[],
	tankCapacity: number,
	startFuel: number,
	reserve: number,
): FuelStop[] {
	const stops: FuelStop[] = []
	const totalMiles = points[points.length - 1]?.mileEnd ?? 0
	let fuel = Math.min(startFuel, tankCapacity)
	let mile = 0

	for (let idx = 0; idx < points.length; idx++) {
		const here = points[idx]
		if (!here) continue

		const fullTankRange = (tankCapacity - reserve) * here.effectiveMpg
		const cheapestIdx = findCheapestInWindow(points, idx, mile, fullTankRange)
		const cheapest = points[cheapestIdx]
		if (!cheapest) continue

		const decision = decideFill(here, cheapest, mile, totalMiles, fuel, tankCapacity, reserve)

		// KNOWN BUG (Codex P2, 2026-04-19) — fix before unshelving:
		// The MIN_FILL_GALLONS guard silently drops *required* small refuels.
		// If a route truly needs (say) 0.33 gal more than a full tank holds,
		// the planner ought to emit a tiny stop (or the prior stop should
		// over-buy by that amount). Currently we skip the stop entirely AND
		// fail to add fuel to the tank, then burn miles anyway — leaving a
		// trip that physically requires a purchase reporting stops:[],
		// totalGallonsBought: 0, smartTotalCost: 0. Repro:
		//   14-gal sedan @ 30 mpg on 430 mi (split 200/200/30) → empty plan
		//
		// Fix direction: only apply MIN_FILL_GALLONS to *optional* top-offs
		// (decision.mustFill === false). When mustFill is true, emit the
		// stop regardless of size — or fold the residual into the prior
		// stop's gallons. Either way, the tank-state arithmetic must not
		// drift from physical reality.
		if (decision.buyGallons >= MIN_FILL_GALLONS) {
			stops.push(
				buildStop(
					stops.length + 1,
					decision.mustFill ? 'forced' : 'smart',
					here,
					mile,
					decision.buyGallons,
				),
			)
			fuel = Math.min(fuel + decision.buyGallons, tankCapacity)
		}

		// Burn through the current state to its end
		fuel -= (here.mileEnd - mile) / here.effectiveMpg
		mile = here.mileEnd
	}

	return stops
}

/**
 * Compute the naive baseline: driver refuels when the tank drops to the
 * threshold (default 25%), filling to capacity at whichever state they
 * happen to be in at that moment.
 *
 * This is what an unplanned driver does in real life — and it's the fair
 * comparison baseline for the smart plan, because both scenarios start
 * with the same fuel and burn the same total gallons.
 */
function computeNaivePlan(
	points: DecisionPoint[],
	tankCapacity: number,
	startFuel: number,
	reserve: number,
): FuelStop[] {
	const stops: FuelStop[] = []
	const refuelThreshold = tankCapacity * NAIVE_REFUEL_THRESHOLD_FRACTION
	let fuel = Math.min(startFuel, tankCapacity)
	let mile = 0

	for (const here of points) {
		const stateEndMile = here.mileEnd
		// Walk through this state in chunks: drive until threshold OR end of state
		while (mile < stateEndMile) {
			const fuelAboveThreshold = Math.max(0, fuel - refuelThreshold)
			const milesUntilRefuel = fuelAboveThreshold * here.effectiveMpg
			const milesRemainingInState = stateEndMile - mile

			if (milesUntilRefuel >= milesRemainingInState) {
				// Can clear this state without refueling
				fuel -= milesRemainingInState / here.effectiveMpg
				mile = stateEndMile
			} else {
				// Drive to refuel point, then fill at this state's price
				fuel -= milesUntilRefuel / here.effectiveMpg
				mile += milesUntilRefuel
				const buyGallons = tankCapacity - fuel
				if (buyGallons >= MIN_FILL_GALLONS) {
					stops.push({
						index: stops.length + 1,
						kind: 'forced',
						stateCode: here.stateCode,
						state: here.state,
						mileFromStart: Math.round(mile),
						pricePerGallon: here.pricePerGallon,
						gallons: Math.round(buyGallons * 10) / 10,
						cost: Math.round(buyGallons * here.pricePerGallon * 100) / 100,
					})
					fuel = tankCapacity
				} else {
					// Threshold reached but tank can't accept a meaningful fill
					// (already nearly full) — keep driving rather than stopping.
					// Avoids an infinite loop in pathological inputs.
					fuel -= milesUntilRefuel / here.effectiveMpg
					mile += 1 // nudge forward to escape the loop
				}
			}
			// Safety reserve never modeled in naive — naive driver runs to threshold,
			// not below. The `reserve` arg is unused for naive but kept in the
			// signature for symmetry with computeSmartPlan.
			void reserve
		}
	}

	return stops
}

/**
 * Plan fuel stops along a route.
 *
 * @param segments - per-state breakdown from the existing fuel engine
 * @param tankCapacityGallons - usable fuel tank capacity
 * @param startingFuelGallons - gallons in the tank at origin (defaults to full)
 */
export function planFuelStops(
	segments: StateSegment[],
	tankCapacityGallons: number,
	startingFuelGallons?: number,
): FuelStopPlan {
	if (segments.length === 0) {
		return {
			stops: [],
			smartTotalCost: 0,
			naiveTotalCost: 0,
			savings: 0,
			totalGallonsBought: 0,
			infeasibleReason: 'No route segments',
		}
	}

	if (tankCapacityGallons <= 0) {
		return {
			stops: [],
			smartTotalCost: 0,
			naiveTotalCost: 0,
			savings: 0,
			totalGallonsBought: 0,
			infeasibleReason: 'Invalid tank capacity',
		}
	}

	const points = buildDecisionPoints(segments)
	const reserve = tankCapacityGallons * SAFETY_RESERVE_FRACTION
	const startFuel = Math.min(startingFuelGallons ?? tankCapacityGallons, tankCapacityGallons)

	const infeasible = checkFeasibility(points, tankCapacityGallons, reserve)
	if (infeasible) {
		return {
			stops: [],
			smartTotalCost: 0,
			naiveTotalCost: 0,
			savings: 0,
			totalGallonsBought: 0,
			infeasibleReason: infeasible,
		}
	}

	const smartStops = computeSmartPlan(points, tankCapacityGallons, startFuel, reserve)
	const naiveStops = computeNaivePlan(points, tankCapacityGallons, startFuel, reserve)

	const smartTotalCost = smartStops.reduce((sum, s) => sum + s.cost, 0)
	const naiveTotalCost = naiveStops.reduce((sum, s) => sum + s.cost, 0)
	const totalGallonsBought = smartStops.reduce((sum, s) => sum + s.gallons, 0)
	// Floor savings at 0 — never tell the user the plan is *worse* than naive.
	// In edge cases the two converge (full tank covers route, single state, etc.)
	const savings = Math.max(0, naiveTotalCost - smartTotalCost)

	return {
		stops: smartStops,
		smartTotalCost: Math.round(smartTotalCost * 100) / 100,
		naiveTotalCost: Math.round(naiveTotalCost * 100) / 100,
		savings: Math.round(savings * 100) / 100,
		totalGallonsBought: Math.round(totalGallonsBought * 10) / 10,
		infeasibleReason: null,
	}
}
